import React from 'react';
import { Box, Typography, Button, TextField, Grid, IconButton } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';

export const Materials = ({ value = [], onChange, editMode = false }) => {
  if (editMode) {
    const handleAddMaterial = () => {
      const newMaterials = [...value, {
        brand: '',
        yarnName: '',
        colorNumber: '',
        colorName: '',
        yardage: null,
        meters: null,
        weightOz: null,
        weightGrams: null,
        fiberContent: '',
        skeins: null
      }];
      onChange('materials', newMaterials);
    };

    const handleChange = (index, field, fieldValue) => {
      const newMaterials = value.map((item, i) => 
        i === index ? { ...item } : item
      );
      
      // Handle empty string as null for numeric fields
      const numericFields = ['yardage', 'meters', 'weightOz', 'weightGrams', 'skeins'];
      const isNumericField = numericFields.includes(field);
      
      if (isNumericField) {
        newMaterials[index][field] = fieldValue === '' ? null : Number(fieldValue);
      } else {
        newMaterials[index][field] = fieldValue;
      }
      
      // Auto-calculate meters from yards and vice versa
      if (field === 'yardage' && fieldValue !== '') {
        const meters = Number(fieldValue) * 0.9144;
        newMaterials[index].meters = Math.round(meters);
      } else if (field === 'meters' && fieldValue !== '') {
        const yards = Number(fieldValue) / 0.9144;
        newMaterials[index].yardage = Math.round(yards);
      }
      
      // Auto-calculate grams from ounces and vice versa
      if (field === 'weightOz' && fieldValue !== '') {
        const grams = Number(fieldValue) * 28.35;
        newMaterials[index].weightGrams = Math.round(grams);
      } else if (field === 'weightGrams' && fieldValue !== '') {
        const oz = Number(fieldValue) / 28.35;
        newMaterials[index].weightOz = parseFloat(oz.toFixed(1));
      }
      
      onChange('materials', newMaterials);
    };

    const handleRemove = (index) => {
      const newMaterials = value.filter((_, i) => i !== index);
      onChange('materials', newMaterials);
    };

    return (
      <Box my={2}>
        <Typography variant="h6" gutterBottom>Yarn</Typography>
        {value.map((material, index) => (
          <Box key={index} p={2} border={1} borderRadius={1} borderColor="divider">
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Brand"
                  value={material.brand || ''}
                  onChange={(e) => handleChange(index, 'brand', e.target.value)}
                  fullWidth
                  margin="dense"
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Yarn Name"
                  value={material.yarnName || ''}
                  onChange={(e) => handleChange(index, 'yarnName', e.target.value)}
                  fullWidth
                  margin="dense"
                  size="small"
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <TextField
                  label="Color #"
                  value={material.colorNumber || ''}
                  onChange={(e) => handleChange(index, 'colorNumber', e.target.value)}
                  fullWidth
                  margin="dense"
                  size="small"
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <TextField
                  label="Color Name"
                  value={material.colorName || ''}
                  onChange={(e) => handleChange(index, 'colorName', e.target.value)}
                  fullWidth
                  margin="dense"
                  size="small"
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <TextField
                  label="Skeins"
                  type="number"
                  value={material.skeins ?? ''}
                  onChange={(e) => handleChange(index, 'skeins', e.target.value)}
                  onWheel={(e) => e.target.blur()}
                  fullWidth
                  margin="dense"
                  size="small"
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <TextField
                  label="Fiber Content"
                  value={material.fiberContent || ''}
                  onChange={(e) => handleChange(index, 'fiberContent', e.target.value)}
                  fullWidth
                  margin="dense"
                  size="small"
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <TextField
                  label="Yardage"
                  type="number"
                  value={material.yardage ? Math.round(material.yardage) : ''}
                  onChange={(e) => handleChange(index, 'yardage', e.target.value)}
                  onWheel={(e) => e.target.blur()}
                  fullWidth
                  margin="dense"
                  size="small"
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <TextField
                  label="Meters"
                  type="number"
                  value={material.meters ? Math.round(material.meters) : ''}
                  onChange={(e) => handleChange(index, 'meters', e.target.value)}
                  onWheel={(e) => e.target.blur()}
                  fullWidth
                  margin="dense"
                  size="small"
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <TextField
                  label="Weight (oz)"
                  type="number"
                  value={material.weightOz ? material.weightOz.toFixed(1) : ''}
                  onChange={(e) => handleChange(index, 'weightOz', e.target.value)}
                  onWheel={(e) => e.target.blur()}
                  fullWidth
                  margin="dense"
                  size="small"
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <TextField
                  label="Weight (g)"
                  type="number"
                  value={material.weightGrams ? Math.round(material.weightGrams) : ''}
                  onChange={(e) => handleChange(index, 'weightGrams', e.target.value)}
                  onWheel={(e) => e.target.blur()}
                  fullWidth
                  margin="dense"
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <IconButton 
                  color="error" 
                  onClick={() => handleRemove(index)}
                  size="small"
                >
                  <DeleteIcon />
                </IconButton>
              </Grid>
            </Grid>
          </Box>
        ))}
        <Button 
          variant="outlined" 
          startIcon={<AddIcon />} 
          onClick={handleAddMaterial}
          sx={{ mt: 1 }}
        >
          Add Yarn
        </Button>
      </Box>
    );
  }

  return (
    <>
      <Typography variant="h6" gutterBottom>Materials</Typography>
      {value.map((material, index) => (
        <Typography key={index}>
          {material.brand} {material.yarnName} ({Math.round(material.yardage)}yds/{Math.round(material.meters)}m, {material.weightOz?.toFixed(1)}oz/{Math.round(material.weightGrams)}g, {material.fiberContent}): {material.skeins} {material.skeins === 1 ? 'skein' : 'skeins'} {material.colorNumber} {material.colorName}
        </Typography>
      ))}
    </>
  );
};

export default Materials;
